import React, { useContext, useRef, useState } from 'react';
import { UploadDocuments } from '../types';
import {
  fetchUploadedFileDetails,
  getUserEnterpriseId,
  getUserIdFromLocalStorage,
  trackUserClicks,
  getValidToken,
} from '../utils';
import { useMsal } from '@azure/msal-react';
import { APIContext } from '../contexts/APIContext';
import useClickOutside from '../hooks/useClickOutside';

interface Project {
  project_id: number;
  project_name: string;
}

const UploadModal = ({ openUploadModal, setOpenUploadModal, setIsUploading }: UploadDocuments) => {
  const { accounts } = useMsal();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const MAX_TOTAL_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
  const projectDropdownRef = useRef(null);

  const {
    websocketConnectionRef: { current: websocket },
    interviewProjectNames,
    fileDetails,
    fileStatus,
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
  } = useContext(APIContext);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setIsDropdownOpen(false);
    setSearchQuery(project.project_name);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedProject(null);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const filteredProjects = interviewProjectNames.filter((project: Project) =>
    project.project_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const checkDuplicateFile = (fileName: string): boolean => {
    const existsInFileDetails = fileDetails?.some((file: any) => file.FileName === fileName);
    const existsInUploadedFiles = uploadedFiles.some((file) => file.name === fileName);
    return existsInFileDetails || existsInUploadedFiles;
  };

  const getTotalFileSize = (files: File[]): number => {
    return files.reduce((total, file) => total + file.size, 0);
  };

  const exceedSizeLimit = (newFiles: File[]): boolean => {
    const currentTotal = getTotalFileSize(uploadedFiles);
    const newFilesTotal = getTotalFileSize(newFiles);
    return currentTotal + newFilesTotal > MAX_TOTAL_FILE_SIZE;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const allowedTypes = ['.xlsx'];
      const validExtensionFiles = filesArray.filter((file) => {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        return allowedTypes.includes(fileExtension);
      });

      const duplicateFiles: string[] = [];
      const validFiles: File[] = [];
      validExtensionFiles.forEach((file) => {
        if (checkDuplicateFile(file.name)) {
          duplicateFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });
      if (exceedSizeLimit(validFiles)) {
        setErrorMessage('Total file size cannot exceed 1MB');
        e.target.value = '';
        return;
      }
      if (duplicateFiles.length > 0) {
        setErrorMessage(` file ${duplicateFiles.join(', ')} already exists`);
      } else {
        setErrorMessage('');
      }
      if (validFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    e.target.value = '';
  };

  const handleDeleteFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setErrorMessage('');
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      const allowedTypes = ['.xlsx'];
      const validFiles = filesArray.filter((file) => {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        return allowedTypes.includes(fileExtension);
      });
      const duplicateFiles: string[] = [];
      const filesToUpload: File[] = [];
      validFiles.forEach((file) => {
        if (checkDuplicateFile(file.name)) {
          duplicateFiles.push(file.name);
        } else {
          filesToUpload.push(file);
        }
      });
      if (exceedSizeLimit(filesToUpload)) {
        setErrorMessage('Total file size cannot exceed 1MB');
        return;
      }
      if (duplicateFiles.length > 0) {
        setErrorMessage(` file ${duplicateFiles.join(', ')} already exists`);
      } else {
        setErrorMessage('');
      }
      if (filesToUpload.length > 0) {
        setUploadedFiles((prev) => [...prev, ...filesToUpload]);
      }
    }
  };

  const handleProceed = async () => {
    const token = await getValidToken();
    setIsUploading(true);
    const filesData = await Promise.all(
      uploadedFiles.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          name: file.name,
          type: file.type,
          size: file.size,
          data: base64,
        };
      })
    );
    const payload = {
      function: 'upload',
      userId: getUserIdFromLocalStorage(),
      EnterpriseId: getUserEnterpriseId(accounts),
      ProjectUId: selectedProject?.project_id || null,
      AccountUId: null,
      files: filesData,
      authorization: `Bearer ${token}`,
    };

    console.log('payload for request save uploaded files: ', payload);
    websocket?.send('SendMessage', payload);
    setOpenUploadModal(false);
    trackUserClicks(websocket, 'DocumentUploaded', userDetails, chatSessionId, WBUId, AppName, accounts, token);
  };
  //convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  useClickOutside(projectDropdownRef, () => {
    setIsDropdownOpen(false);
  });
  return (
    <div
      id="upload-status-Modal"
      className={`c-modal ${openUploadModal ? '-show' : ''}`}
      role="dialog"
      tabIndex={openUploadModal ? 0 : -1}
      aria-labelledby="modalDialogTitle"
      aria-modal="true"
    >
      <div
        className="c-modal__overlay -dismiss-modal"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'none' }}
      ></div>
      <div className="c-modal__container -size-medium">
        <div className="c-modal__content c-new-upload-modal" style={{ backgroundColor: '#fff' }}>
          <div className="c-modal__header justify-items-start">
            <div className="c-modal__header-inner">
              <div className="c-modal__header-right">
                <button
                  type="button"
                  className="c-modal__header-close -dismiss-modal"
                  aria-label="Close"
                  title="Close"
                  onClick={() => setOpenUploadModal(false)}
                ></button>
              </div>
            </div>
            <div className="c-modal__header-inner">
              <div className="c-modal__header-left">
                <div
                  className="c-modal__title c-new_upload_modal__header-title"
                  role="heading"
                  id="modalDialogTitle"
                  aria-level={2}
                >
                  Upload New Document
                </div>
              </div>
            </div>
            <p className="c-new-upload-modal__header-cont">
              Upload your documents and proceed to ask your questions to generate insights
            </p>
          </div>
          <div className="c-modal__body c-new-upload-modal__mainbody">
            <div className="c-new-upload-modal__drop justify-items-start">
              <div className="c-new-upload-modal__drop-hint">Project*</div>
              <div className="c-tab-content -widthcontent text-left">
                <div className="c-tab-content__input-text">
                  <div className="o-multi-select-two " tabIndex={0} ref={projectDropdownRef}>
                    <div className="o-multi-select-two__head ">
                      <div className="o-input-wrap ">
                        <i className="icon-search"></i>
                        <input
                          type="text"
                          className="o-input -dropdown"
                          id="input1"
                          name="input1"
                          placeholder="Search & Select Project..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                        />
                      </div>
                      <i
                        className={`o-multi-select-two__arrow icon-caret ${isDropdownOpen ? '-active' : ''}`}
                        tabIndex={0}
                        onClick={toggleDropdown}
                      ></i>
                    </div>
                    <div className={`o-multi-select-two__dropdown ${isDropdownOpen ? '-active' : ''}`}>
                      <ul className="o-multi-select-two__list justify-text-start">
                        {filteredProjects.length > 0 ? (
                          filteredProjects.map((project: Project) => (
                            <a
                              href="javascript:;"
                              key={project.project_id}
                              role="option"
                              className="c-dropdown-item"
                              id="sample-dropdown-option-1"
                              aria-selected="false"
                              onClick={() => handleSelectProject(project)}
                            >
                              {project.project_name}
                            </a>
                          ))
                        ) : (
                          <li className="c-dropdown-item">No results found</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`c-new-upload-modal__upload ${isDragging ? '-dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div>
                <i className="c-new-upload-modal__icon icon-uploading" aria-hidden="true"></i>
              </div>
              <div className="c-new-upload-modal__file-upload">
                <p className="c-new-upload-modal__tab-drag">
                  Drag and drop files here or{' '}
                  <a href="javascript:void(0);">
                    <label className="c-new-upload-modal__browse cursor-pointer">
                      <input type="file" multiple accept=".xlsx" className="hidden" onChange={handleFileUpload} />
                      Browse
                    </label>
                  </a>
                </p>
                <p className="c-new-upload-modal__file-size">Max Total file size: 1 MB | File should be xlsx only</p>
              </div>
            </div>
            {errorMessage && (
              <div className="c-new-upload-modal__error text-left" style={{ color: '#d32f2f', fontSize: '10px' }}>
                {errorMessage}
              </div>
            )}
          </div>
          <div className="c-new-upload-modal__progresscards -customscroll">
            {uploadedFiles.length > 0 &&
              uploadedFiles.map((file, index) => (
                <div key={index} className="c-new-upload-modal__itemflex">
                  <div className="c-new-upload-modal__projectdetails">
                    <div className="c-new-upload-modal__projectname">{file.name}</div>
                    <div className="c-new-upload-modal__status">
                      <i className="icon-circle-tick"></i> Ready for query
                    </div>
                  </div>
                  <div className="c-new-upload-modal__progressflex">
                    <span className="c-new-upload-modal__filesize text-xs text-[#737373] mr-2">
                      {(file.size / (1024 * 1024)).toFixed(2) + ' MB'}
                    </span>
                    <button className="c-new-upload-modal__deleteprogress" onClick={() => handleDeleteFile(index)}>
                      <i className="icon-delete"></i>
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="c-modal__footer">
            <div className="c-modal__footer-btnGroup">
              <button
                type="button"
                className="o-secondary-button -dismiss-modal"
                aria-label="Cancel"
                onClick={() => setOpenUploadModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="o-primary-button -dismiss-modal"
                aria-label="Proceed"
                onClick={handleProceed}
                disabled={uploadedFiles.length === 0 || !selectedProject}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
